// The workbench's thread with its agent (RFC-057 model 6): what the person
// asked, the agent's reply as Markdown as it is written, its thoughts and
// tool calls folded as the timeline folds them, and a permission it asks
// for answered in place. Below, the prompt: Enter sends, Shift+Enter a new
// line; while a turn runs, Stop cancels it. Timeline shows the share's
// turns in its place, every agent's, as the others in the share see them.
// Each reply is headed with the agent's name, a tool call is one line of
// what it did to what and how it went.
import { Fragment, useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { t, whenMinute, whenShort, word } from "../i18n";
import { Markdown } from "../markdown";
import { ChatForm } from "./Chat";
import { Popover } from "./Popover";
import { agentLabel, type PastThread, type PermissionItem, type Project, type Thread, type ThreadItem, type ToolItem } from "../workbench";

export function AgentThread({
  thread,
  agent,
  agentName,
  agentNames,
  agentLocked,
  onAgent,
  disabled,
  why,
  onPrompt,
  onCancel,
  onChoose,
  onNewThread,
  root,
  project,
  projects,
  onProject,
  history,
  onGoBack,
  timeline,
  inputRef,
  inRoom = false,
  context,
}: {
  thread: Thread;
  /** what the agent is doing, as workbench/agent says it */
  agent: { state: string; error?: string };
  /** the agent the thread is with, and those the workbench has (RFC-057 D5) */
  agentName: string;
  agentNames: string[];
  /** an exam's question: its attempt's agent, not to be changed */
  agentLocked: boolean;
  onAgent: (name: string) => void;
  disabled: boolean;
  /** why the prompt takes nothing now, said in it while it is disabled */
  why?: string;
  onPrompt: (text: string) => boolean;
  onCancel: () => void;
  onChoose: (p: PermissionItem, optionId: string) => void;
  onNewThread: () => void;
  /** the work tree as a person reads it */
  root: string;
  /** the folder the thread works in, "" for the work tree */
  project: string;
  /** an exam's questions in the tree */
  projects: Project[];
  onProject: (path: string) => void;
  /** the folder's threads to go back to, asked for as the list opens */
  history: () => Promise<PastThread[]>;
  onGoBack: (x: PastThread) => void;
  /** the share's timeline, its turns with every agent (RFC-057 D6), shown in the thread's place while asked for */
  timeline?: ReactNode;
  /** the prompt's field, for the page to focus (Ask the agent, from an empty editor) */
  inputRef?: RefObject<HTMLTextAreaElement>;
  /** an exam's room begun (RFC-058): the question picked is the folder, so none is asked for here */
  inRoom?: boolean;
  /** what the thread is about, before its title in the switcher: the exam's question shown */
  context?: string;
}) {
  // the folder's threads, read each time the switcher opens; null while read
  const [past, setPast] = useState<PastThread[] | null>(null);
  const [showTimeline, setShowTimeline] = useState(false);
  // the thread shown, named as a chat names one: by what was first asked
  const firstAsk = thread.items.find((x) => x.kind === "user");
  const title = firstAsk?.kind === "user" ? firstAsk.text : t("workbench:newThread");
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => {
    end.current?.scrollIntoView?.({ block: "end" });
  }, [thread.items.length, thread.items[thread.items.length - 1]]);

  const agentStateLabel = agent.state === "running" ? t("workbench:agentRunning") : t("workbench:agentIdle");
  // the folder picker only where there is a folder to pick: the work tree
  // alone is the tree beside it, said once
  // — and none in the exam room, whose question picked is the folder
  const pickFolder = !inRoom && (project !== "" || projects.length > 0);

  return (
    <section className="wb-thread" aria-label={t("workbench:thread")}>
      {/* the pane's first line: who the thread is with, picked here where
          there is more than one, in the accent, and how it is */}
      <div className="wb-agent-bar">
        <span className="wb-agent-pick-box">
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
            <path d="M8 1.5l1.6 4.4 4.4 1.6-4.4 1.6L8 13.5l-1.6-4.4L2 7.5l4.4-1.6Z" fill="currentColor" />
          </svg>
          {/* a picker only where the agent may change: an exam's is fixed, said by name */}
          {agentNames.length > 1 && !agentLocked ? (
            <>
              <select
                className="wb-agent-pick"
                aria-label={t("workbench:agentPick")}
                value={agentName}
                disabled={disabled || thread.busy}
                onChange={(e) => onAgent(e.target.value)}
              >
                {agentNames.map((n) => (
                  <option key={n} value={n}>
                    {agentLabel(n)}
                  </option>
                ))}
              </select>
              {/* the picker's own chevron, inside the pill: the browser's sat on its edge */}
              <svg className="wb-agent-pick-chevron" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
                <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </>
          ) : (
            <strong className="wb-agent-name">{t("workbench:agentName", { name: agentLabel(agentName) })}</strong>
          )}
        </span>
        <span className="grow" />
        {/* stopped is the one state to act on, a chip; ready and running are the rows' state dot and a word */}
        {agent.state === "stopped" ? (
          <span className="chip chip-failed">{t("workbench:agentStopped")}</span>
        ) : (
          <span className={`wb-agent-state${agent.state === "running" ? " is-live" : ""}`}>
            <span className="state-dot" aria-hidden="true" />
            {agentStateLabel}
          </span>
        )}
      </div>
      {/* what acts on the thread (byj-1wv.51.23): the thread shown, by its
          first question, opening the folder's others to go back to; outside
          an exam, the share's timeline; then a new one */}
      <div className="wb-thread-head">
        {showTimeline ? (
          <button className="small ghost wb-thread-back" onClick={() => setShowTimeline(false)}>
            ← {t("workbench:backToThread")}
          </button>
        ) : (
          <Popover
            label={t("workbench:history")}
            button={
              <>
                <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
                  <path d="M2.5 3.5h11v7h-6l-3 2.5v-2.5h-2Z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
                </svg>
                <span className="wb-thread-switch-title">{context ? `${context} · ${title}` : title}</span>
                <svg className="wb-thread-switch-chevron" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
                  <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </>
            }
            buttonLabel={t("workbench:threadSwitch", { title })}
            buttonClassName="small wb-thread-switch"
            className="pop wb-thread-switch-box"
            popClassName="pop-panel wb-history-pop"
            focus="button:not(:disabled)"
            disabled={disabled}
            onOpen={() => {
              setPast(null);
              history().then(setPast, () => setPast([]));
            }}
          >
            {(close) => (
              <div className="wb-history-view">
                <p className="wb-history-head">
                  <strong>{t("workbench:history")}</strong>
                  {past !== null && <span className="muted small">{t("workbench:historyCount", { count: past.length })}</span>}
                </p>
                {past === null ? (
                  <p className="muted small wb-history-none">{t("common:loading")}</p>
                ) : (
                  <ul className="wb-history">
                    {past.length === 0 && <li className="muted small wb-history-none">{t("workbench:historyEmpty")}</li>}
                    {past.map((x) => {
                      const current = x.sessionId === thread.sessionId;
                      const state = current ? "current" : x.state === "open" ? (x.busy ? "working" : "open") : x.state;
                      const label =
                        state === "current"
                          ? t("workbench:historyCurrent")
                          : state === "working"
                            ? t("workbench:historyWorking")
                            : state === "open"
                              ? t("workbench:historyOpen")
                              : state === "elsewhere"
                                ? t("workbench:historyElsewhere")
                                : "";
                      return (
                        <li key={x.sessionId}>
                          <button
                            className={`wb-history-item wb-history-${state}`}
                            aria-current={current ? "true" : undefined}
                            disabled={disabled || thread.busy || current || x.state === "elsewhere"}
                            onClick={() => {
                              close();
                              onGoBack(x);
                            }}
                          >
                            {/* the title the row's width; when and how it stands under it */}
                            <span className="wb-history-title">{x.title || t("workbench:historyUntitled")}</span>
                            <span className="wb-history-meta">
                              {label && <span className={`chip${state === "current" || state === "working" ? " chip-live" : ""}`}>{label}</span>}
                              {x.updatedAt && <span title={whenMinute(x.updatedAt)}>{whenShort(x.updatedAt)}</span>}
                              {x.turns !== undefined && <span>{t("workbench:historyTurns", { count: x.turns })}</span>}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </Popover>
        )}
        {/* the share's turns as the others in it see them, in the thread's
            place: in sight, beside the thread; not the candidate's in an exam */}
        {timeline && !inRoom && (
          <button className="small timeline-toggle" aria-pressed={showTimeline} title={t("workbench:timelineTitle")} onClick={() => setShowTimeline(!showTimeline)}>
            {t("workbench:timeline")}
          </button>
        )}
        <button
          className="small wb-thread-new"
          disabled={disabled || thread.busy}
          onClick={() => {
            setShowTimeline(false);
            onNewThread();
          }}
        >
          <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
            <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          {t("workbench:newThread")}
        </button>
      </div>
      {showTimeline && <div className="wb-timeline">{timeline}</div>}
      {/* the thread, unless the timeline is shown in its place */}
      {!showTimeline && (
        <>
          {pickFolder && (
            <label className="wb-thread-project small">
              <span className="muted">{t("workbench:workingIn")}</span>
              <select value={project} disabled={disabled || thread.busy} onChange={(e) => onProject(e.target.value)}>
                <option value="">{root || "~"}</option>
                {projects.map((p) => (
                  <option key={p.path} value={p.path}>
                    {t("workbench:examQuestion", { exam: p.exam, question: p.question })}
                  </option>
                ))}
                {project && !projects.some((p) => p.path === project) && <option value={project}>{project}</option>}
              </select>
            </label>
          )}
          {!inRoom && projects.length > 0 && !projects.some((p) => p.path === project) && <p className="banner small">{t("workbench:examPick")}</p>}
          {agent.state === "stopped" && agent.error && <p className="banner error small">{agent.error}</p>}
          <div className="wb-thread-items" aria-live="polite">
            {thread.items.length === 0 && <p className="muted wb-thread-empty">{t("workbench:threadEmpty", { name: agentLabel(agentName) })}</p>}
            {thread.items.map((x, i) => (
              <Fragment key={i}>
                {/* a reply begins where the person's prompt ends: headed with who answers */}
                {x.kind !== "user" && (i === 0 || thread.items[i - 1]!.kind === "user") && <p className="wb-reply-head">{agentLabel(agentName)}</p>}
                <Item item={x} name={agentLabel(agentName)} disabled={disabled} onChoose={onChoose} />
              </Fragment>
            ))}
            {thread.busy && (
              <p className="muted small wb-thread-working">
                <span className="wb-working-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
                {t("workbench:working", { name: agentLabel(agentName) })}
              </p>
            )}
            <div ref={end} />
          </div>
        </>
      )}
      {/* the side panel's card to write in: Enter as a chat sends it, a touch
          keyboard's and an IME's syllable-ending Enter included. Hidden, not
          taken away, while the timeline is shown, so what is half written
          is there again after it */}
      <div className="wb-thread-prompt-box" hidden={showTimeline}>
        <ChatForm
          className="wb-thread-prompt"
          label={t("workbench:promptLabel", { name: agentLabel(agentName) })}
          placeholder={t("workbench:promptPlaceholder", { name: agentLabel(agentName) })}
          sendLabel={t("workbench:send")}
          disabled={disabled}
          why={why}
          onSend={(text) => !thread.busy && onPrompt(text)}
          stop={thread.busy ? { label: t("workbench:stop"), onStop: onCancel } : undefined}
          inputRef={inputRef}
        />
      </div>
    </section>
  );
}

function Item({ item, name, disabled, onChoose }: { item: ThreadItem; name: string; disabled: boolean; onChoose: (p: PermissionItem, optionId: string) => void }) {
  switch (item.kind) {
    case "user":
      return (
        <div className="wb-thread-user">
          <p>{item.text}</p>
        </div>
      );
    case "agent":
      return (
        <div className="wb-thread-agent">
          <Markdown text={item.text} />
        </div>
      );
    case "thought":
      return (
        <Fold className="item thought" head={<span className="muted">{t("workbench:thought")}</span>}>
          <p className="muted">{item.text}</p>
        </Fold>
      );
    case "error":
      return <p className="banner error small">{item.text}</p>;
    case "tool":
      return <Tool item={item} />;
    case "permission":
      return (
        <div className="wb-thread-permission" role="group" aria-label={t("workbench:permissionAsked", { name })}>
          <p>
            <strong>{t("workbench:permissionAsked", { name })}</strong> <code>{item.title}</code>
          </p>
          {item.chosen === undefined ? (
            <div className="row">
              {item.options.map((o) => (
                <button
                  key={o.optionId}
                  className={`small${o.kind?.startsWith("allow") ? " primary" : ""}`}
                  data-kind={o.kind}
                  disabled={disabled}
                  onClick={() => onChoose(item, o.optionId)}
                >
                  {optionLabel(o.optionId, o.name)}
                </button>
              ))}
            </div>
          ) : (
            <p className="muted small">{t("workbench:permissionChosen", { option: optionLabel(item.chosen) })}</p>
          )}
        </div>
      );
  }
}

/** byj acp's four options in the page's words, another agent's as it names them. */
function optionLabel(id: string, name?: string): string {
  switch (id) {
    case "allow-once":
      return t("workbench:allowOnce");
    case "allow-always":
      return t("workbench:allowAlways");
    case "reject-once":
      return t("workbench:rejectOnce");
    case "reject-always":
      return t("workbench:rejectAlways");
  }
  return name ?? id;
}

/**
 * A tool call as one line: how it went as a dot (its words to a screen
 * reader), what kind of thing it did, and to what; unfolded, its arguments
 * and result.
 */
function Tool({ item }: { item: ToolItem }) {
  const state = item.status === "completed" ? "ok" : item.status === "failed" ? "failed" : "running";
  const args = item.input === undefined ? "" : JSON.stringify(item.input, null, 2);
  const how = state === "running" ? t("session:running") : state === "failed" ? t("session:failed") : t("session:ok");
  return (
    <Fold
      className={`item tool tool-${state} wb-tool`}
      head={
        <>
          <span className={`wb-tool-state wb-tool-${state}`} title={how}>
            <span className="sr-only">{how}</span>
          </span>
          <span className="wb-tool-kind">{word("workbench:toolKind", item.toolKind) ?? t("workbench:toolKind.other")}</span>
          <code className="tool-arg-line">{item.title}</code>
        </>
      }
    >
      {args && args !== "{}" && <pre className="tool-args">{args}</pre>}
      {item.output !== undefined && (
        <div className="tool-result">
          <div className="tool-result-head">{t("session:toolResult")}</div>
          <pre>{item.output}</pre>
        </div>
      )}
    </Fold>
  );
}

function Fold({ className, head, children }: { className: string; head: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={className}>
      <button type="button" className="item-head fold-head" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {head}
      </button>
      {open && <div className="fold-body">{children}</div>}
    </div>
  );
}
